import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "src/store/features/api";
import { AnyObject } from "yup/lib/types";
import { useAppDispatch } from "src/store/store";
import {
  setPlaceOfBusinessAddress,
  useOnboarding,
  PlaceOfBusinessAddressType,
  setSameAsRegisteredOfficeAddress,
} from "src/store/features/onboarding.slice";
import { Checkbox, Grid, InputAdornment, List, ListItemButton, Typography } from "@mui/material";
import { SearchAddress, StyledTextField } from "./styled-components/AddressFields";
import { Field, useFormikContext } from "formik";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { PlaceOfBusinessAddressValues } from "./utils/formSchema";
import _ from "lodash";
import { Label } from "./SelectAccountType";

interface GoogleAutocompleteProps {
  onPlaceSelected?: (place: any) => void;
}

export const StyledEndAdorment = (props: { selected: boolean }) => {
  return (
    <InputAdornment position="end">
      <div style={{ display: props.selected ? "flex" : "none", width: "100%" }}>
        <GridCheckCircleIcon sx={{ fill: "#002035", fontSize: "16px" }} />
      </div>
    </InputAdornment>
  );
};

const GoogleAutocomplete: React.FC<GoogleAutocompleteProps> = ({ onPlaceSelected }) => {
  const [input, setInput] = useState<string>("");
  const [hide, setHide] = useState<boolean>(false);
  const [delayedInput, setDelayedInput] = useState(input);
  const { data } = api.useLookUpAddressFromInputQuery<{
    data: { predictions: AnyObject[]; status: string };
  }>(
    { input: delayedInput },
    {
      skip: !delayedInput || delayedInput.includes(","),
    },
  );

  const debouncedChangeHandler = useCallback(
    _.debounce((input) => setDelayedInput(input), 300),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHide(false);
    const inputVal = encodeURIComponent(e.target.value);
    setInput(inputVal);
    debouncedChangeHandler(inputVal);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setInput(suggestion.description);
    setHide(true);
    if (onPlaceSelected) {
      onPlaceSelected(suggestion);
    }
  };

  const selected = data?.predictions.length > 0;

  return (
    <>
      <Field
        as={SearchAddress}
        selected={selected}
        InputProps={{
          endAdornment: <StyledEndAdorment selected={selected} />,
        }}
        value={decodeURIComponent(input)}
        onChange={handleInputChange}
        placeholder="Search places..."
      />
      {!hide && data?.predictions.length > 0 && (
        <List dense={true}>
          {data.predictions.map((suggestion: any) => (
            <ListItemButton
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion.description}
            </ListItemButton>
          ))}
        </List>
      )}
    </>
  );
};

const RekycAddressLookUp2: React.FC = () => {
  const [getDetails] = api.useGetPlaceDetailMutation();
  const dispatch = useAppDispatch();

  const handlePlaceSelected = (place: any) => {
    // You can further type 'place' based on the Google Places API response.
    const place_id = place.place_id as string;

    getDetails({ getPlaceDetailsDto: { placeid: place_id } })
      .unwrap()
      .then((res: any) => {
        const {
          result: { formatted_address, address_components },
        } = res;

        const getAddressComponent = (type: string, components: any[], abbreviated?: boolean) => {
          const component = components.find((item: any) => item.types.includes(type));
          if (abbreviated) return component ? component.short_name : null;
          return component ? component.long_name : null;
        };

        const details: PlaceOfBusinessAddressType = {
          placeOfBusinessFullAddress: formatted_address,
          placeOfBusinessCountry: getAddressComponent("country", address_components),
          placeOfBusinessPostCode: getAddressComponent("postal_code", address_components),
          placeOfBusinessCity: getAddressComponent("locality", address_components),
          council: getAddressComponent("administrative_area_level_2", address_components),
          placeOfBusinessStreet: getAddressComponent("route", address_components),
          placeOfBusinessStreetNumber: getAddressComponent("street_number", address_components),
          placeOfBusinessunitApartment: getAddressComponent("subpremise", address_components),
          placeOfBusinessState: getAddressComponent(
            "administrative_area_level_1",
            address_components,
            true,
          ),
        };
        dispatch(setPlaceOfBusinessAddress(details));
      });
  };

  const { values, errors } = useFormikContext<PlaceOfBusinessAddressValues>();
  const {
    placeOfBusinessCountry: countryErrorMessage,
    placeOfBusinessStreet: streetAddressErrorMessage,
    placeOfBusinessunitApartment: unitApartmentErrorMessage,
    placeOfBusinessState: stateErrorMessage,
    placeOfBusinessPostCode: postCodeErrorMessage,
  } = errors;

  const {
    placeOfBusinessCountry,
    placeOfBusinessState,
    placeOfBusinessPostCode,
    placeOfBusinessStreet,
    placeOfBusinessunitApartment,
  } = values;

  const prevValues = useRef(values);

  useEffect(() => {
    // Update redux state on manual input after checking for pottential infinite loop
    if (
      prevValues.current.placeOfBusinessStreet !== values.placeOfBusinessStreet ||
      prevValues.current.placeOfBusinessunitApartment !== values.placeOfBusinessunitApartment ||
      prevValues.current.placeOfBusinessCountry !== values.placeOfBusinessCountry ||
      prevValues.current.placeOfBusinessState !== values.placeOfBusinessState ||
      prevValues.current.placeOfBusinessPostCode !== values.placeOfBusinessPostCode
    ) {
      const details = {
        placeOfBusinessFullAddress: values.placeOfBusinessStreet,
        placeOfBusinessCountry: values.placeOfBusinessCountry,
        placeOfBusinessPostCode: values.placeOfBusinessPostCode,
        placeOfBusinessunitApartment: values.placeOfBusinessunitApartment,
        placeOfBusinesssState: values.placeOfBusinessState,
      };

      dispatch(setPlaceOfBusinessAddress(details));
    }

    prevValues.current = values;
  }, [values, dispatch]);

  const {
    sameAsRegisteredOfficeAddress,
    registeredOfficeAddress: {
      registeredOfficeFullAddress,
      registeredOfficeCountry,
      registeredOfficeState,
      registeredOfficePostcode,
      registeredOfficeunitApartment,
      registeredOfficeCity,
    },
  } = useOnboarding();

  useEffect(() => {
    if (sameAsRegisteredOfficeAddress) {
      const details: Partial<PlaceOfBusinessAddressType> = {
        placeOfBusinessFullAddress: registeredOfficeFullAddress,
        placeOfBusinessCountry: registeredOfficeCountry,
        placeOfBusinessPostCode: registeredOfficePostcode,
        placeOfBusinessunitApartment: registeredOfficeunitApartment,
        placeOfBusinessState: registeredOfficeState,
        placeOfBusinessCity: registeredOfficeCity,
      };
      dispatch(setPlaceOfBusinessAddress(details));
    } else {
      const details: Partial<PlaceOfBusinessAddressType> = {
        placeOfBusinessFullAddress: "",
        placeOfBusinessCountry: "",
        placeOfBusinessPostCode: "",
        placeOfBusinessunitApartment: "",
        placeOfBusinessState: "",
        placeOfBusinessCity: "",
      };
      dispatch(setPlaceOfBusinessAddress(details));
    }
  }, [sameAsRegisteredOfficeAddress]);

  return (
    <>
      <Grid container spacing={1} mb={1}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", justifyContent: "space-between" }}
          alignItems={"center"}>
          <Label text="Place of Business Address" />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          display={"flex"}
          alignItems={"center"}
          sx={{ justifyContent: { xs: "none", md: "end" } }}>
          <Checkbox
            disabled={!registeredOfficeFullAddress}
            checked={sameAsRegisteredOfficeAddress ? true : false}
            onChange={(e) => dispatch(setSameAsRegisteredOfficeAddress(e.target.checked))}
            sx={{ color: "#000000" }}
          />
          <Typography>Same as registered office address</Typography>
        </Grid>
        {!sameAsRegisteredOfficeAddress && (
          <Grid item xs={12} mb={1}>
            <GoogleAutocomplete onPlaceSelected={handlePlaceSelected} />
          </Grid>
        )}
      </Grid>
      {!sameAsRegisteredOfficeAddress && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Field
              placeholder="Country of Residence"
              error={!!countryErrorMessage}
              helperText={countryErrorMessage}
              name="placeOfBusinessCountry"
              as={StyledTextField}
              selected={!!placeOfBusinessCountry}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!placeOfBusinessCountry} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              placeholder="State"
              error={!!stateErrorMessage}
              helperText={stateErrorMessage}
              as={StyledTextField}
              name="placeOfBusinessState"
              selected={!!placeOfBusinessState}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!placeOfBusinessState} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              placeholder="Postcode"
              error={!!postCodeErrorMessage}
              helperText={postCodeErrorMessage}
              as={StyledTextField}
              name="placeOfBusinessPostCode"
              selected={!!placeOfBusinessPostCode}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!placeOfBusinessPostCode} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              placeholder="Street Address"
              error={!!streetAddressErrorMessage}
              helperText={streetAddressErrorMessage}
              as={StyledTextField}
              name="placeOfBusinessStreet"
              selected={!!placeOfBusinessStreet}
              InputProps={{
                endAdornment: <StyledEndAdorment selected={!!placeOfBusinessStreet} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              placeholder="(Optional) Unit/Apartment no."
              as={StyledTextField}
              name="placeOfBusinessunitApartment"
              error={!!unitApartmentErrorMessage}
              helperText={unitApartmentErrorMessage}
              selected={!!placeOfBusinessunitApartment && !unitApartmentErrorMessage}
              InputProps={{
                endAdornment: (
                  <StyledEndAdorment
                    selected={!!placeOfBusinessunitApartment && !unitApartmentErrorMessage}
                  />
                ),
              }}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default RekycAddressLookUp2;
